import React, { useMemo, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import { Stack, Typography, Box } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import OverviewDialog from './OverviewDialog';
import UserAvatar from '../../../common/UserAvatar';
import RnRBadge from '../../../assets/Gifs/trophy.gif';

const RnRNominations = ({ data, currentQuarter }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNomination, setSelectedNomination] = useState(null);

  const handleOpenDialog = (nomination) => {
    setSelectedNomination(nomination);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedNomination(null);
    setOpenDialog(false);
  };

  const columns = [
    {
      name: 'index',
      label: 'No',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => <span>{tableMeta.rowIndex + 1}</span>,
      },
    },
    {
      name: 'employeeName',
      label: 'Employee Name',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const row = tableMeta.rowData;
          const photo = row[4];
          const isWinner = row[9];
          return (
            <Stack direction={'row'} alignItems={'center'}>
              <UserAvatar src={photo} />
              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                <Typography
                  noWrap
                  fontSize={'16px'}
                  sx={{
                    display: 'flex',
                    maxWidth: '300px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {value}
                </Typography>
                {isWinner ? (
                  <img
                    alt='RnR winner'
                    src={RnRBadge}
                    style={{ width: 30, height: 30, marginLeft: '0.3rem', mixBlendMode: 'multiply' }}
                  />
                ) : (
                  <></>
                )}
              </Box>
            </Stack>
          );
        },
      },
    },
    {
      name: 'quarter',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'year',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'employeePhotoPath',
      label: 'Photo',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'nominationCategory',
      label: 'Category',
      options: {
        filter: true,
        filterOptions: { fullWidth: true },
        sort: true,
      },
    },
    {
      name: 'nominatedBy',
      label: 'Nominated By',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'quarterDetails',
      label: 'Quarter Details',
      options: {
        filter: true,
        filterOptions: { fullWidth: true },
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => {
          const year = tableMeta.rowData[3];
          const quarter = tableMeta.rowData[2];
          return (
            <Stack direction={'row'}>
              <Typography fontSize={'14px'}>
                {year} - Q{quarter}
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: 'dateNominated',
      label: 'Date Nominated',
      options: {
        sort: true,
        filter: false,
      },
    },
    {
      name: 'isWinner',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Stack spacing={2} direction={'row'}>
              <Visibility
                onClick={() => handleOpenDialog(tableData[tableMeta.rowIndex])}
                sx={{ cursor: 'default' }}
                aria-label='view'
              />
              {/* Add other actions here if needed */}
            </Stack>
          );
        },
      },
    },
  ];

  // Map the data for the data table columns
  const tableData = useMemo(() => {
    let formattedData =
      data?.map((nomination) => ({
        employeeName: nomination.isTeamNomination
          ? nomination.nomineeName?.join(', ')
          : nomination.nomineeName || 'N/A',
        nominationCategory: nomination.categoryName,
        nominatedBy: nomination.nominatorName || 'N/A',
        dateNominated: new Date(nomination.dateNominated).toLocaleDateString(),
        rawDateNominated: new Date(nomination.dateNominated),
        _id: nomination._id,
        categoryId: nomination.categoryId,
        nomineeEmpCode: nomination.nomineeEmpCode,
        quarter: nomination.time.quarter,
        year: nomination.time.year,
        quarterDetails: nomination.time.quarter + ' ' + nomination.time.year,
        employeePhotoPath: nomination.isTeamNomination ? 'team.gif' : nomination.nomineePhotoPath,
        isWinner: nomination.isWinner,
        nominationReason: nomination.nominationReason,
        attachment: nomination.attachment,
        teamName: nomination.isTeamNomination ? nomination.teamName[0] : null,
        isTeamNomination: nomination.isTeamNomination,
      })) || [];

    // Sort the data so that winners appear first
    formattedData.sort((a, b) => new Date(b.rawDateNominated) - new Date(a.rawDateNominated));

    return formattedData;
  }, [data]);

  return (
    <>
      <MUIDataTable
        title={
          <Box display={'flex'} justifyContent={'start'} alignItems={'center'} gap={1}>
            <Typography fontSize={'16px'} fontWeight={'bold'}>
              RnR Details
            </Typography>
            {currentQuarter?.data?.active ? (
              <Typography fontSize={'12px'} sx={{ color: 'grey' }}>
                Current quarter automatically gets stop on {currentQuarter?.endDate}
              </Typography>
            ) : (
              <Typography fontSize={'12px'} sx={{ color: 'grey' }}>
                Next Quarter will gets start automatically on {currentQuarter?.nextQuarterStartDate}
              </Typography>
            )}
          </Box>
        }
        data={tableData}
        columns={columns}
        options={{
          selectableRows: false,
          rowsPerPage: 20,
          rowsPerPageOptions: [20, 30, 40],
          viewColumns: false,
          search: true,
          searchOpen: false,
          download: false,
          filter: true,
          print: false,
          sort: false,
          tableBodyMaxHeight: '74vh',
        }}
        className='custom-mui-datatable'
      />
      <style>
        {`
          .custom-mui-datatable {
            background-color: transparent !important;
            box-shadow: none !important;
          }
        `}
      </style>
      {selectedNomination && (
        <OverviewDialog open={openDialog} handleClose={handleCloseDialog} nomination={selectedNomination} />
      )}
    </>
  );
};

export default RnRNominations;
